import { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Box, Button, Chip, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Paper, Snackbar, Tooltip, Typography } from '@mui/material';
import {
  AdminPanelSettings,
  Badge as BadgeIcon,
  CheckCircle,
  Close,
  Folder,
  History,
  LocationOn,
  Login,
  Mail,
  MoreVert,
  Phone,
  VpnKey,
  Warning,
  Delete,
} from '@mui/icons-material';
import Moment from 'moment';
import { useMutation, useQuery } from '@apollo/client/react';
import { useConfig } from '../../components/Configuration';
import { formatDateOnly } from '../../Formatters';
import { GET_BOUNCED, GET_LAST_RESET_PASSWORD_URL, REMOVE_BOUNCE } from '../user.graphql';
import { Block } from '@mui/icons-material';
import { Config } from '../../config';
import DeleteUsers from '../overview/DeleteUsers';

interface HeroUser {
  id: number;
  loginId?: string | null;
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  lastLoggedIn?: string | null;
  createdAt?: string | null;
  isActive: boolean;
  isAdminuserOnActiveLicense: boolean;
  address?: { city?: string | null } | null;
}

interface Props {
  user?: HeroUser | null;
  isActive: boolean;
  totalAccesses: number;
  licenseGroupCount: number;
  adminLicenseCount: number;
}

function relativeFromNow(iso?: string | null): string {
  if (!iso) return '–';
  return Moment.utc(iso).fromNow();
}

// lastLoggedIn comes from the API as a UTC timestamp; render it in Norwegian local time.
function formatLocalDate(iso?: string | null): string {
  if (!iso) return '';
  return Moment.utc(iso).local().format('DD.MM.YYYY HH:mm');
}

function yearsSince(iso?: string | null): string {
  if (!iso) return '–';
  const years = Math.floor(Moment().diff(Moment(iso), 'years', true));
  return `${years} år`;
}

function getLoginAsUserUrl(config: Config | undefined, user?: HeroUser | null) {
  if (!config || !user) return undefined;
  if (config.featureToggleChangeUserNewPortal) {
    return `${config.portalUrl}/impersonate/${user.loginId}`;
  } else {
    return `${config.bksUrl}/Administration/LoginAsAnotherUser/${user.loginId}`
  }
}

export function Hero({ user, isActive, totalAccesses, licenseGroupCount, adminLicenseCount }: Props) {
  const config = useConfig();
  const navigate = useNavigate();
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [moreAnchor, setMoreAnchor] = useState<null | HTMLElement>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const wasDeleted = useRef(false);

  const { data: bouncedData } = useQuery<{ bounced: { id: number; isBounced: boolean; reason: string } }>(GET_BOUNCED, {
    variables: { id: user?.id ?? 0 },
    skip: !user,
  });
  const isBounced = bouncedData?.bounced?.isBounced ?? false;
  const bounceReason = bouncedData?.bounced?.reason ?? '';

  const [removeBounce] = useMutation<any, any>(REMOVE_BOUNCE, { variables: { input: { id: user?.id ?? 0 } } });
  const canRemoveBounce = !!config?.featureToggleRemoveBounce;

  const { data: resetUrlData } = useQuery<{ url: string | null }>(GET_LAST_RESET_PASSWORD_URL, {
    variables: { id: user?.id ?? 0 },
    skip: !user,
  });
  const resetUrl = resetUrlData?.url || null;

  const initials = useMemo(() => {
    const first = (user?.firstName?.[0] ?? '').toUpperCase();
    const last = (user?.lastName?.[0] ?? '').toUpperCase();
    return `${first}${last}` || '?';
  }, [user?.firstName, user?.lastName]);


  const loginAsUserUrl = getLoginAsUserUrl(config, user);

  const handleCopyResetUrl = async () => {
    if (!resetUrl || !navigator.clipboard) return;
    await navigator.clipboard.writeText(resetUrl);
    setCopySnackbarOpen(true);
  };

  return (
    <Paper
      elevation={1}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 2,
        background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
        color: 'primary.contrastText',
        px: { xs: 2.5, sm: 3, md: 4 },
        py: { xs: 2.5, sm: 3, md: 3.5 },
      }}>
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          right: -80,
          top: -80,
          width: 320,
          height: 320,
          borderRadius: '50%',
          background: (theme) =>
            `radial-gradient(circle, ${theme.alpha(theme.palette.common.white, 0.08)} 0%, ${theme.alpha(theme.palette.common.white, 0)} 70%)`,
          pointerEvents: 'none',
        }}
      />

      <Box sx={{ position: 'relative', display: 'flex', gap: { xs: 2, sm: 3 }, alignItems: 'center' }}>
        <Avatar
          sx={{
            width: { xs: 56, sm: 72, md: 88 },
            height: { xs: 56, sm: 72, md: 88 },
            flexShrink: 0,
            bgcolor: 'primary.dark',
            color: 'primary.contrastText',
            typography: { xs: 'h6', sm: 'h5', md: 'h4' },
          }}>
          {initials}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.75, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {isActive ? (
                <BadgePill tone="success" icon={<CheckCircle />}>
                  Aktiv
                </BadgePill>
              ) : (
                <BadgePill tone="error" icon={<Close />}>
                  Inaktiv
                </BadgePill>
              )}
              {user?.isAdminuserOnActiveLicense && (
                <BadgePill tone="primary" icon={<AdminPanelSettings />}>
                  Administrator
                </BadgePill>
              )}
              {isBounced && (
                <Tooltip title={bounceReason || 'E-post-adressen er blokkert'} placement="top">
                  <span>
                    <BadgePill tone="warning" icon={<Warning />}>
                      E-post bouncer
                    </BadgePill>
                  </span>
                </Tooltip>
              )}
            </Box>
          </Box>

          <Typography component="h1" sx={{ typography: { xs: 'h5', md: 'h4' }, wordBreak: 'break-word' }}>
            {user?.displayName || `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || '—'}
          </Typography>

          <Box
            sx={{
              mt: 0.75,
              display: 'flex',
              gap: { xs: 1, sm: 2.25 },
              flexWrap: 'wrap',
              alignItems: 'center',
              typography: 'body2',
              color: (theme) => theme.alpha(theme.palette.primary.contrastText, 0.85),
              minWidth: 0,
            }}>
            {user?.email && <ContactChip icon={<Mail fontSize="inherit" />}>{user.email}</ContactChip>}
            {user?.phone && <ContactChip icon={<Phone fontSize="inherit" />}>{user.phone}</ContactChip>}
            {user?.address?.city && <ContactChip icon={<LocationOn fontSize="inherit" />}>{user.address.city}</ContactChip>}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: { xs: 2, md: 3 },
          mt: { xs: 2, md: 2.75 },
          pt: { xs: 2, md: 2.5 },
          borderTop: 1,
          borderColor: (theme) => theme.alpha(theme.palette.primary.contrastText, 0.18),
        }}>
        <Stat
          icon={<Folder fontSize="inherit" />}
          label="Tilganger"
          value={String(totalAccesses)}
          sub={`på ${licenseGroupCount} lisens${licenseGroupCount === 1 ? '' : 'er'}`}
        />
        <Stat
          icon={<AdminPanelSettings fontSize="inherit" />}
          label="Administrerer"
          value={String(adminLicenseCount)}
          sub={adminLicenseCount === 1 ? 'lisens' : 'lisenser'}
        />
        <Stat
          icon={<History fontSize="inherit" />}
          label="Sist pålogget"
          value={relativeFromNow(user?.lastLoggedIn)}
          sub={formatLocalDate(user?.lastLoggedIn)}
        />
        <Stat
          icon={<BadgeIcon fontSize="inherit" />}
          label="Medlem i"
          value={yearsSince(user?.createdAt)}
          sub={user?.createdAt ? formatDateOnly(user.createdAt) : ''}
        />
      </Box>

      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          gap: 1.25,
          flexWrap: 'wrap',
          mt: { xs: 2, md: 2.5 },
          pt: { xs: 2, md: 2.5 },
          borderTop: 1,
          borderColor: (theme) => theme.alpha(theme.palette.primary.contrastText, 0.18),
        }}>
        <Button
          component="a"
          href={loginAsUserUrl}
          target="_blank"
          rel="noreferrer"
          disabled={!loginAsUserUrl}
          variant="contained"
          disableElevation
          startIcon={<Login />}
          sx={{
            bgcolor: 'background.paper',
            color: 'primary.dark',
            '&:hover': { bgcolor: 'grey.100' },
          }}>
          Logg inn som bruker
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="outlined"
          color="inherit"
          onClick={(e) => setMoreAnchor(e.currentTarget)}
          startIcon={<MoreVert />}>
          Flere valg
        </Button>
        <Menu anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={() => setMoreAnchor(null)}>
          {resetUrl && (
            <MenuItem
              onClick={() => {
                handleCopyResetUrl();
                setMoreAnchor(null);
              }}>
              <ListItemIcon>
                <VpnKey fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Kopiér lenke til nytt passord" />
            </MenuItem>
          )}
          {isBounced && (
            <MenuItem
              disabled={!canRemoveBounce}
              onClick={() => {
                removeBounce();
                setMoreAnchor(null);
              }}>
              <ListItemIcon>
                <Block fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Fjern e-post-blokkering"
              />
            </MenuItem>
          )}
          <MenuItem
            disabled={!user || user.isAdminuserOnActiveLicense}
            title={user?.isAdminuserOnActiveLicense ? 'Brukere som administrerer aktive lisenser kan ikke slettes.' : undefined}
            onClick={() => {
              setDeleteOpen(true);
              setMoreAnchor(null);
            }}>
            <ListItemIcon>
              <Delete fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText primary="Slett bruker" slotProps={{ primary: { sx: { color: 'error.main' } } }} />
          </MenuItem>
        </Menu>

        {deleteOpen && user && (
          <DeleteUsers
            open={deleteOpen}
            onClose={() => {
              setDeleteOpen(false);
              if (wasDeleted.current) navigate('/user');
            }}
            userId={[user.id]}
            users={[{ id: user.id, displayName: user.displayName ?? '' }]}
            setSelected={() => { }}
            removeFromList={() => {
              wasDeleted.current = true;
            }}
          />
        )}
      </Box>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={copySnackbarOpen}
        autoHideDuration={3000}
        onClose={() => setCopySnackbarOpen(false)}
        message="Lenken ble kopiert"
        action={
          <IconButton color="inherit" size="small" onClick={() => setCopySnackbarOpen(false)}>
            <Close fontSize="small" />
          </IconButton>
        }
      />
    </Paper>
  );
}

export function ContactChip({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        minWidth: 0,
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
      {icon}
      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {children}
      </Box>
    </Box>
  );
}

export type Tone = 'success' | 'primary' | 'warning' | 'error';

// Soft, tinted pill that stays readable on top of the primary-coloured hero.
export function BadgePill({ tone, icon, children }: { tone: Tone; icon: React.ReactElement; children: React.ReactNode }) {
  return (
    <Chip
      size="small"
      icon={icon}
      label={children}
      sx={{
        bgcolor: (theme) => theme.lighten(theme.palette[tone].main, 0.85),
        color: `${tone}.dark`,
        '& .MuiChip-icon': { color: 'inherit' },
      }}
    />
  );
}

export function Stat({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string; sub?: string }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="overline"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          lineHeight: 1.5,
          color: (theme) => theme.alpha(theme.palette.primary.contrastText, 0.7),
        }}>
        {icon}
        {label}
      </Typography>
      <Typography variant="h6" sx={{ mt: 0.25, wordBreak: 'break-word' }}>
        {value}
      </Typography>
      {sub && (
        <Typography
          variant="caption"
          component="div"
          sx={{ mt: 0.25, color: (theme) => theme.alpha(theme.palette.primary.contrastText, 0.6) }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
}
